import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Pie } from 'react-chartjs-2';
import { getCountryName } from '../../../utils/countryNamesClient';
import { useAdminTheme } from '../ui/useAdminTheme';
import { formatVerificationRate, loginFrequencyLabel } from './analyticsFormat';
import { activityUserId, geoMapPoints } from './countryCentroids';
import { registerAdminCharts } from './registerAdminCharts';

registerAdminCharts();

const LeafletPointsMap = lazy(() => import('../../../components/LeafletPointsMap'));

export function GeographicSection({ geoData = [], cities = [] }) {
  const { theme } = useAdminTheme();
  const totalLoginSessions = geoData.reduce((sum, item) => sum + item.count, 0);
  const points = geoMapPoints(geoData);

  return (
    <div className="analytics-section">
      <h2>Geographic Distribution</h2>
      <div className="chart">
        <h2>Login sessions by country</h2>
        <p style={{ marginBottom: '1rem', color: 'var(--vx-admin-muted)', fontSize: '0.9rem' }}>
          Login sessions by country in the selected UTC window (not unique users). Marker size follows volume.
        </p>
        {points.length ? (
          <div className="admin-geo-map-wrap">
            <Suspense fallback={<div className="admin-geo-map-fallback">Loading map…</div>}>
              <LeafletPointsMap
                center={[20, 10]}
                zoom={2}
                className="admin-geo-map"
                points={points.map((point) => ({
                  ...point,
                  popup: `${getCountryName(point.code)}: ${point.count} session${point.count === 1 ? '' : 's'}`,
                }))}
                theme={theme}
                fitBounds
                markerVariant="dot"
                scrollWheelZoom={false}
              />
            </Suspense>
          </div>
        ) : (
          <div className="admin-geo-map-fallback">No mappable country data in this window.</div>
        )}
        <div className="geo-table">
          {geoData.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Login Sessions</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {geoData.slice(0, 20).map((item, index) => (
                  <tr key={`${item._id || 'unknown'}-${index}`}>
                    <td>{getCountryName(item._id) || 'Unknown'}</td>
                    <td>{item.count}</td>
                    <td>{totalLoginSessions > 0 ? ((item.count / totalLoginSessions) * 100).toFixed(1) : '0.0'}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--vx-admin-muted)' }}>
              No geographical data available
            </div>
          )}
        </div>
        {cities.length ? (
          <p className="adminMutedText" style={{ marginTop: 12, fontSize: 13 }}>
            Top cities: {cities.slice(0, 6).map((city) => `${city.city || 'Unknown'} (${city.count})`).join(' · ')}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function DevicesSection({ deviceStatsData }) {
  return (
    <div className="analytics-section">
      <h2>Device Analytics</h2>
      <div className="chart">
        <h2>Device Usage Distribution</h2>
        <p style={{ marginBottom: '1rem', color: 'var(--vx-admin-muted)', fontSize: '0.9rem' }}>
          Sessions in the selected UTC window.
        </p>
        {!deviceStatsData?.empty ? (
          <Pie
            data={deviceStatsData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'bottom' },
              },
            }}
          />
        ) : null}
        {deviceStatsData?.empty ? (
          <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--vx-admin-muted)' }}>
            No device data available
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function SessionsSection({ analyticsData }) {
  const logins = analyticsData.recentActivity?.recentLogins || [];
  return (
    <div className="analytics-section">
      <h2>Session Analytics</h2>
      <div className="metrics-grid">
        <div className="metric-item">
          <span className="metric-value">{analyticsData.sessions?.onlineNow || 0}</span>
          <span className="metric-label">Online Now</span>
        </div>
        <div className="metric-item">
          <span className="metric-value">{analyticsData.sessions?.avgDurationMinutes || 0}</span>
          <span className="metric-label">Avg Duration (min)</span>
        </div>
        <div className="metric-item">
          <span className="metric-value">{analyticsData.sessions?.todayCount ?? analyticsData.sessions?.sessionsToday ?? 0}</span>
          <span className="metric-label">Sessions Today</span>
        </div>
        <div className="metric-item">
          <span className="metric-value">{analyticsData.sessions?.totalSessions || 0}</span>
          <span className="metric-label">Total Sessions</span>
        </div>
      </div>

      {logins.length ? (
        <div className="activity-card">
          <h3>Recent Logins</h3>
          {logins.slice(0, 10).map((login, index) => {
            const userId = activityUserId(login);
            const inner = (
              <>
                <span>{login.user?.email || 'Unknown'}</span>
                <span>{login.loginAt ? new Date(login.loginAt).toLocaleString() : ''}</span>
              </>
            );
            return userId ? (
              <Link key={`${userId}-${login.loginAt || index}`} to={`/admin/users?user=${encodeURIComponent(userId)}`} className="activity-item activity-item-link">
                {inner}
              </Link>
            ) : (
              <div key={`${login.user?.email || 'unknown'}-${login.loginAt || index}`} className="activity-item">
                {inner}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function TopActiveUsers({ users = [] }) {
  if (!users.length) return null;
  return (
    <div className="analytics-section">
      <h2>Top active users</h2>
      <div className="activity-card">
        <p className="adminMutedText" style={{ marginTop: 0, fontSize: 13 }}>
          Most sessions in the selected UTC window.
        </p>
        {users.slice(0, 10).map((user, index) => {
          const userId = activityUserId(user);
          const inner = (
            <>
              <span>{user.name ? `${user.name} · ${user.email}` : (user.email || 'Unknown')}</span>
              <span>{user.sessionCount} session{user.sessionCount === 1 ? '' : 's'}</span>
            </>
          );
          return userId ? (
            <Link key={userId} to={`/admin/users?user=${encodeURIComponent(userId)}`} className="activity-item activity-item-link">
              {inner}
            </Link>
          ) : (
            <div key={`${user.email || 'user'}-${index}`} className="activity-item">{inner}</div>
          );
        })}
      </div>
    </div>
  );
}

function hourRows(activityByHour = []) {
  const byHour = new Map(activityByHour.map((row) => [Number(row._id), Number(row.count) || 0]));
  return Array.from({ length: 24 }, (_, hour) => ({ hour, count: byHour.get(hour) || 0 }));
}

export function EngagementSection({ engagement }) {
  if (!engagement) return null;
  const retention = engagement.retention || {};
  const rate = formatVerificationRate(retention.retentionRate);
  const hours = hourRows(engagement.activityByHour);
  const maxHour = Math.max(1, ...hours.map((row) => row.count));
  const frequency = engagement.loginFrequency || [];
  const maxFreq = Math.max(1, ...frequency.map((row) => Number(row.count) || 0));
  const thisPeriod = retention.thisPeriod ?? retention.thisWeek;
  const lastPeriod = retention.lastPeriod ?? retention.lastWeek;

  return (
    <div className="analytics-section">
      <h2>Engagement</h2>
      <div className="metrics-grid">
        <div className="metric-item">
          <span className="metric-value">{rate}</span>
          <span className="metric-label">Returning users</span>
        </div>
        <div className="metric-item">
          <span className="metric-value">{retention.retained || 0}</span>
          <span className="metric-label">Active in both windows</span>
        </div>
        <div className="metric-item">
          <span className="metric-value">{thisPeriod || 0}</span>
          <span className="metric-label">Active this window</span>
        </div>
        <div className="metric-item">
          <span className="metric-value">{lastPeriod || 0}</span>
          <span className="metric-label">Active prior window</span>
        </div>
      </div>

      <div className="chart" style={{ minHeight: 0 }}>
        <h2>Logins by hour (UTC)</h2>
        <div className="admin-hour-bars" role="img" aria-label="Logins by hour of day in UTC">
          {hours.map((row) => (
            <div key={row.hour} className="admin-hour-bar" title={`${String(row.hour).padStart(2, '0')}:00 UTC · ${row.count} logins`}>
              <span style={{ height: `${Math.max(row.count ? 8 : 2, (row.count / maxHour) * 100)}%` }} />
              {(row.hour % 6 === 0) ? <em>{row.hour}</em> : <em />}
            </div>
          ))}
        </div>
      </div>

      {frequency.length ? (
        <div className="chart" style={{ minHeight: 0 }}>
          <h2>Login frequency</h2>
          <p className="adminMutedText" style={{ marginTop: 0, fontSize: 13 }}>How many times each user signed in during this window.</p>
          <div className="admin-freq-list">
            {frequency.map((row) => (
              <div key={String(row._id)} className="admin-freq-row">
                <span>{loginFrequencyLabel(row._id)}</span>
                <div className="admin-freq-track">
                  <i style={{ width: `${Math.max(6, (Number(row.count) / maxFreq) * 100)}%` }} />
                </div>
                <strong>{row.count}</strong>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
